/**
 * Bot Detection Logic
 * Multi-layered detection for automation tools, headless browsers, and bot-like behavior
 */

import type {
  BotSignal,
  BotDetectionResult,
  BehaviorState,
  FingerprintData,
  ScrollEvent,
  HoverEvent,
  ClickEvent,
} from './types';

/**
 * Generate a cryptographically secure random nonce
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Simple hash function for fingerprinting
 */
async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ============================================================================
// Layer 1: Automation Tool Detection
// ============================================================================

/**
 * Detect common automation tools and headless browsers
 */
export function detectAutomationTools(): BotSignal[] {
  const signals: BotSignal[] = [];
  const nav = navigator as Navigator & {
    webdriver?: boolean;
  };
  const win = window as Window & {
    __playwright?: unknown;
    __nightmare?: unknown;
    callPhantom?: unknown;
    _phantom?: unknown;
    phantom?: unknown;
  };

  // Check navigator.webdriver (Selenium/Puppeteer)
  if (nav.webdriver === true) {
    signals.push('webdriver');
  }

  // Check for Playwright
  if (win.__playwright !== undefined) {
    signals.push('playwright');
  }

  // Check for Nightmare.js
  if (win.__nightmare !== undefined) {
    signals.push('nightmare');
  }

  // Check for PhantomJS
  if (
    win.callPhantom !== undefined ||
    win._phantom !== undefined ||
    win.phantom !== undefined
  ) {
    signals.push('phantom');
  }

  return signals;
}

/**
 * Detect headless browser indicators
 */
export function detectHeadlessBrowser(): BotSignal[] {
  const signals: BotSignal[] = [];
  const nav = navigator;

  // Check for missing plugins (headless browsers often have none)
  if (!nav.plugins || nav.plugins.length === 0) {
    signals.push('missing_plugins');
  }

  // Check for missing languages
  if (!nav.languages || nav.languages.length === 0) {
    signals.push('missing_languages');
  }

  // Check WebGL renderer for SwiftShader (headless Chrome indicator)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        if (typeof renderer === 'string' && renderer.toLowerCase().includes('swiftshader')) {
          signals.push('swiftshader');
        }
      }
    }
  } catch {
    // WebGL not available, not necessarily a bot signal
  }

  // Check for screen dimension mismatches (only extreme cases)
  if (window.innerWidth === 0 || window.innerHeight === 0) {
    signals.push('screen_mismatch');
  }

  // Check for outer dimensions being significantly smaller than inner (impossible normally)
  // Allow some tolerance for browser chrome and dev tools
  const widthDiff = window.innerWidth - window.outerWidth;
  const heightDiff = window.innerHeight - window.outerHeight;

  // Only flag if difference is substantial (> 50px indicates suspicious behavior)
  if (widthDiff > 50 || heightDiff > 50) {
    signals.push('screen_mismatch');
  }

  return signals;
}

// ============================================================================
// Layer 2: Browser Fingerprinting
// ============================================================================

/**
 * Generate canvas fingerprint
 */
export function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');

    if (!ctx) return 'no-canvas';

    // Draw text with specific styles
    ctx.textBaseline = 'alphabetic';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('BotDetect123', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('BotDetect123', 4, 17);

    // Draw shapes
    ctx.beginPath();
    ctx.arc(50, 25, 10, 0, Math.PI * 2);
    ctx.fill();

    return canvas.toDataURL();
  } catch {
    return 'canvas-error';
  }
}

/**
 * Generate WebGL fingerprint
 */
export function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return 'no-webgl';

    const webglCtx = gl as WebGLRenderingContext;
    const debugInfo = webglCtx.getExtension('WEBGL_debug_renderer_info');

    const data = {
      vendor: debugInfo
        ? webglCtx.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'unknown',
      renderer: debugInfo
        ? webglCtx.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'unknown',
      version: webglCtx.getParameter(webglCtx.VERSION),
      shadingLanguageVersion: webglCtx.getParameter(webglCtx.SHADING_LANGUAGE_VERSION),
      extensions: webglCtx.getSupportedExtensions()?.join(',') || '',
    };

    return JSON.stringify(data);
  } catch {
    return 'webgl-error';
  }
}

/**
 * Generate audio context fingerprint
 */
export function getAudioFingerprint(): Promise<string> {
  return new Promise((resolve) => {
    try {
      const AudioContext = window.AudioContext || (window as Window & { webkitAudioContext?: typeof window.AudioContext }).webkitAudioContext;
      if (!AudioContext) {
        resolve('no-audio');
        return;
      }

      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const analyser = context.createAnalyser();
      const gain = context.createGain();
      const scriptProcessor = context.createScriptProcessor(4096, 1, 1);

      gain.gain.value = 0; // Mute the audio
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(10000, context.currentTime);

      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gain);
      gain.connect(context.destination);

      let fingerprint = '';
      scriptProcessor.onaudioprocess = (event) => {
        const data = event.inputBuffer.getChannelData(0);
        fingerprint = data.slice(0, 100).join(',');

        oscillator.disconnect();
        scriptProcessor.disconnect();
        gain.disconnect();
        context.close();

        resolve(fingerprint || 'audio-empty');
      };

      oscillator.start(0);

      // Timeout fallback
      setTimeout(() => {
        if (!fingerprint) {
          context.close();
          resolve('audio-timeout');
        }
      }, 1000);
    } catch {
      resolve('audio-error');
    }
  });
}

/**
 * Get screen and environment properties
 */
export function getScreenFingerprint(): string {
  const data = {
    width: screen.width,
    height: screen.height,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
  };
  return JSON.stringify(data);
}

/**
 * Generate complete browser fingerprint
 */
export async function generateFingerprint(): Promise<FingerprintData> {
  const [audioFp] = await Promise.all([getAudioFingerprint()]);

  return {
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint(),
    audio: audioFp,
    screen: getScreenFingerprint(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: navigator.platform,
  };
}

/**
 * Detect fingerprint anomalies
 */
export function detectFingerprintAnomalies(fingerprint: FingerprintData): BotSignal[] {
  const signals: BotSignal[] = [];

  // Canvas anomaly: no canvas support or error
  if (fingerprint.canvas === 'no-canvas' || fingerprint.canvas === 'canvas-error') {
    signals.push('canvas_anomaly');
  }

  // WebGL anomaly: no WebGL or error
  if (fingerprint.webgl === 'no-webgl' || fingerprint.webgl === 'webgl-error') {
    signals.push('webgl_anomaly');
  }

  // Audio anomaly: no audio context or error
  if (
    fingerprint.audio === 'no-audio' ||
    fingerprint.audio === 'audio-error' ||
    fingerprint.audio === 'audio-timeout'
  ) {
    signals.push('audio_anomaly');
  }

  return signals;
}

// ============================================================================
// Layer 3: Behavior Analysis
// ============================================================================

/**
 * Analyze mouse movement patterns for human-like behavior
 */
export function analyzeMouseMovement(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];
  const movements = state.mouseMovements;

  // No mouse movement after significant time
  const timeSincePageLoad = Date.now() - state.pageLoadTime;
  if (movements.length === 0 && timeSincePageLoad > 5000) {
    signals.push('no_mouse_movement');
  }

  return signals;
}

/**
 * Analyze keyboard timing for human-like patterns
 */
export function analyzeKeyboardTiming(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];
  const timings = state.keyboardTimings;

  if (timings.length < 5) {
    return signals;
  }

  // Calculate interval variance
  const intervals = timings.map((t) => t.interval).filter((i) => i > 0);
  if (intervals.length < 3) {
    return signals;
  }

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const variance =
    intervals.reduce((sum, i) => sum + Math.pow(i - avgInterval, 2), 0) / intervals.length;
  const stdDev = Math.sqrt(variance);

  // Very low standard deviation indicates robotic typing
  // Humans typically have high variance in typing speed
  const coefficientOfVariation = stdDev / avgInterval;
  if (coefficientOfVariation < 0.1) {
    signals.push('robotic_keyboard');
  }

  return signals;
}

/**
 * Check for instant interactions (bots act too fast)
 */
export function checkInteractionTiming(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];

  if (state.firstInteractionTime !== null) {
    const timeSincePageLoad = state.firstInteractionTime - state.pageLoadTime;
    // First interaction less than 50ms after page load is suspicious
    // Reduced from 100ms to avoid false positives on fast page loads
    if (timeSincePageLoad < 50) {
      signals.push('instant_interaction');
    }
  }

  return signals;
}

// ============================================================================
// Layer 4: Aggressive Behavioral Analysis
// ============================================================================

/**
 * Detect instant form filling (< 500ms total)
 * Bots often fill forms instantaneously
 */
export function detectInstantFormFill(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];

  if (state.formFillStartTime && state.formFillEndTime) {
    const fillDuration = state.formFillEndTime - state.formFillStartTime;
    // Filling a form in under 500ms with multiple keystrokes is superhuman
    if (fillDuration < 500 && state.keyboardTimings.length >= 5) {
      signals.push('instant_form_fill');
    }
  }

  return signals;
}

/**
 * Detect no scrolling on scrollable pages
 * Real users scroll on long pages
 */
export function detectNoScrollActivity(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];
  const timeSincePageLoad = Date.now() - state.pageLoadTime;

  // Only check on pages that require scrolling (1.5x viewport height)
  const pageHeight = document.documentElement.scrollHeight;
  const viewportHeight = window.innerHeight;

  if (pageHeight > viewportHeight * 1.5 && timeSincePageLoad > 5000) {
    if (state.scrollEvents.length === 0) {
      signals.push('no_scroll_activity');
    }
  }

  return signals;
}

/**
 * Detect clicks without prior hover
 * Real users typically hover before clicking
 */
export function detectClickWithoutHover(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];

  const clicksWithoutHover = state.clickEvents.filter(c => !c.hadPriorHover);
  const totalClicks = state.clickEvents.length;

  // If more than 50% of clicks had no hover, suspicious
  if (totalClicks >= 3 && clicksWithoutHover.length / totalClicks > 0.5) {
    signals.push('click_without_hover');
  }

  return signals;
}

/**
 * Detect clicks without prior mouse movement
 * Bots often click without natural mouse approach
 */
export function detectNoMouseBeforeClick(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];

  const clicksWithoutMove = state.clickEvents.filter(c => !c.hadPriorMouseMove);
  const totalClicks = state.clickEvents.length;

  if (totalClicks >= 2 && clicksWithoutMove.length / totalClicks > 0.5) {
    signals.push('no_mouse_before_click');
  }

  return signals;
}

/**
 * Detect zero intervals between keystrokes (instant filling)
 * Bots can type with 0ms intervals
 */
export function detectZeroKeystrokeIntervals(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];
  const timings = state.keyboardTimings;

  if (timings.length < 5) return signals;

  // Count intervals that are 0 or near-zero (< 10ms)
  const zeroIntervals = timings.filter(t => t.interval >= 0 && t.interval < 10).length;
  const ratio = zeroIntervals / (timings.length - 1);

  // If more than 30% of intervals are near-zero, it's instant filling
  if (ratio > 0.3) {
    signals.push('zero_keystroke_intervals');
  }

  return signals;
}

/**
 * Detect superhuman typing speed (> 20 chars/second = 50ms/char)
 * Average human typing is 5-7 chars/second
 */
export function detectSuperhumanTyping(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];
  const timings = state.keyboardTimings;

  if (timings.length < 10) return signals;

  const intervals = timings.map(t => t.interval).filter(i => i > 0);
  if (intervals.length < 5) return signals;

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;

  // Average interval < 50ms means typing > 20 chars/second (superhuman)
  if (avgInterval < 50) {
    signals.push('superhuman_typing');
  }

  return signals;
}

/**
 * Detect programmatic form filling (Playwright .fill() etc)
 * Only triggers if a form was actually interacted with but no keyboard events occurred
 */
export function detectProgrammaticFormFill(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];

  // Only trigger if a form field was actually focused (formFillStartTime is set)
  // This prevents false positives from simple page navigation without typing
  if (state.formFillStartTime && state.keyboardTimings.length === 0) {
    // A form was focused but no keys were pressed - likely programmatic fill
    const timeSinceFormFocus = Date.now() - state.formFillStartTime;
    // Give user at least 2 seconds to start typing after focusing a form
    if (timeSinceFormFocus > 2000) {
      signals.push('programmatic_form_fill');
    }
  }

  return signals;
}

/**
 * Detect straight-line mouse movement (no natural curves)
 * Human mouse movement has acceleration/deceleration and curves
 */
export function detectStraightLineMouse(state: BehaviorState): BotSignal[] {
  const signals: BotSignal[] = [];
  const movements = state.mouseMovements;

  if (movements.length < 10) return signals;

  // Calculate how "straight" the movements are
  let straightSegments = 0;

  for (let i = 2; i < movements.length; i++) {
    const p1 = movements[i - 2];
    const p2 = movements[i - 1];
    const p3 = movements[i];

    // Calculate the angle change between segments
    const angle1 = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    const angle2 = Math.atan2(p3.y - p2.y, p3.x - p2.x);
    const angleDiff = Math.abs(angle1 - angle2);

    // If angle change is very small (< 0.1 radians ≈ 6 degrees), it's a straight segment
    if (angleDiff < 0.1) {
      straightSegments++;
    }
  }

  const straightRatio = straightSegments / (movements.length - 2);

  // If more than 80% of movements are in straight lines, suspicious
  if (straightRatio > 0.8 && movements.length > 20) {
    signals.push('straight_line_mouse');
  }

  return signals;
}

// ============================================================================
// Main Detection Function
// ============================================================================

/**
 * Signal weights for bot score calculation
 */
const SIGNAL_WEIGHTS: Record<BotSignal, number> = {
  // Automation tools (high confidence)
  webdriver: 50,
  playwright: 50,
  nightmare: 50,
  phantom: 50,

  // Environment anomalies
  missing_plugins: 10,
  missing_languages: 10,
  swiftshader: 50,       // Increased - strong headless indicator
  screen_mismatch: 20,

  // Fingerprint anomalies
  canvas_anomaly: 15,
  webgl_anomaly: 15,
  audio_anomaly: 10,

  // Basic behavioral signals
  // NOTE: These low-confidence signals must NOT sum to ≥70 on their own, since legitimate
  // keyboard/touch/touchpad users commonly trigger several of them simultaneously.
  // Only high-confidence signals (automation tools, honeypots) should be able to cross
  // the threshold alone.
  no_mouse_movement: 15,      // Reduced from 25 - keyboard/touch users have no mouse events
  robotic_keyboard: 35,
  instant_interaction: 15,

  // Honeypot signals (high confidence)
  honeypot_triggered: 80,
  honeypot_link_clicked: 80,

  // Aggressive behavioral signals
  instant_form_fill: 40,        // Form filled in <500ms total
  no_scroll_activity: 10,       // Reduced from 20 - many backoffice pages don't need scrolling
  click_without_hover: 10,      // Reduced from 15 - very common with keyboard/touch navigation
  straight_line_mouse: 30,      // Mouse moved in perfect straight line
  zero_keystroke_intervals: 15, // Keystrokes with 0ms between them (password managers trigger this)
  no_mouse_before_click: 10,    // Reduced from 15 - very common with keyboard/touch navigation
  superhuman_typing: 35,        // Typing faster than 20 chars/second
  programmatic_form_fill: 45,   // Form filled without any keyboard events (Playwright .fill())
};

/**
 * Calculate bot score from signals
 */
function calculateBotScore(signals: BotSignal[]): number {
  const totalWeight = signals.reduce((sum, signal) => sum + SIGNAL_WEIGHTS[signal], 0);
  return Math.min(100, totalWeight);
}

/**
 * Run all bot detection checks
 */
export async function runBotDetection(
  behaviorState: BehaviorState,
  honeypotSignals: BotSignal[] = []
): Promise<BotDetectionResult> {
  const signals: BotSignal[] = [];

  // Layer 1: Automation tools
  signals.push(...detectAutomationTools());
  signals.push(...detectHeadlessBrowser());

  // Layer 2: Fingerprint anomalies
  const fingerprint = await generateFingerprint();
  signals.push(...detectFingerprintAnomalies(fingerprint));

  // Layer 3: Basic behavior analysis
  signals.push(...analyzeMouseMovement(behaviorState));
  signals.push(...analyzeKeyboardTiming(behaviorState));
  signals.push(...checkInteractionTiming(behaviorState));

  // Layer 4: Honeypot signals (passed from context)
  signals.push(...honeypotSignals);

  // Layer 5: Aggressive behavioral analysis (catches MCP Playwright-like bots)
  signals.push(...detectInstantFormFill(behaviorState));
  signals.push(...detectNoScrollActivity(behaviorState));
  signals.push(...detectClickWithoutHover(behaviorState));
  signals.push(...detectNoMouseBeforeClick(behaviorState));
  signals.push(...detectZeroKeystrokeIntervals(behaviorState));
  signals.push(...detectSuperhumanTyping(behaviorState));
  signals.push(...detectStraightLineMouse(behaviorState));
  signals.push(...detectProgrammaticFormFill(behaviorState));

  // Calculate fingerprint hash
  const fingerprintHash = await hashString(JSON.stringify(fingerprint));

  const score = calculateBotScore(signals);
  const uniqueSignals = [...new Set(signals)];

  // Debug logging for bot detection
  console.log('[BotDetection] Score:', score, 'Signals:', uniqueSignals);
  console.log('[BotDetection] Behavior state:', {
    mouseMovements: behaviorState.mouseMovements.length,
    keyboardTimings: behaviorState.keyboardTimings.length,
    scrollEvents: behaviorState.scrollEvents.length,
    hoverEvents: behaviorState.hoverEvents.length,
    clickEvents: behaviorState.clickEvents.length,
    formFillStart: behaviorState.formFillStartTime,
    formFillEnd: behaviorState.formFillEndTime,
  });

  return {
    score,
    signals: uniqueSignals,
    fingerprint: fingerprintHash,
    timestamp: Date.now(),
    nonce: generateNonce(),
  };
}

/**
 * Create initial behavior state
 */
export function createInitialBehaviorState(): BehaviorState {
  return {
    mouseMovements: [],
    keyboardTimings: [],
    firstInteractionTime: null,
    pageLoadTime: Date.now(),
    // Enhanced tracking fields
    scrollEvents: [],
    hoverEvents: [],
    clickEvents: [],
    formFillStartTime: null,
    formFillEndTime: null,
  };
}
